import {
  adminTabFeatureId,
  navFeatureId,
  productTabFeatureId,
  SPREADSHEET_COLUMN_PREFIX,
} from '@atlas/shared';

export const FEATURE_SURFACES = ['nav', 'productTab', 'adminTab', 'home'] as const;
export type FeatureSurface = (typeof FEATURE_SURFACES)[number];

// Re-exported, not redefined: the backend gates routes on these same ids now
// that Feature Visibility decides who reaches a page, so the literals have one
// home (`@atlas/shared/feature-visibility`).
export { navFeatureId, productTabFeatureId, adminTabFeatureId };
export const homeSectionFeatureId = (value: string): string => `home:${value}`;
/**
 * A single Products Spreadsheet column, by field key.
 *
 * Deliberately NOT a `FeatureSurface`: the other four surfaces are static lists
 * this module can enumerate, but the column set is built at runtime from the
 * admin-defined metadata fields. The visibility page fetches those and renders
 * the toggles itself; `HIDEABLE_FEATURES` cannot hold them.
 */
export const spreadsheetColumnFeatureId = (key: string): string =>
  `${SPREADSHEET_COLUMN_PREFIX}${key}`;

/**
 * Features that must never be hidden:
 * - the Admin Panel nav item and the Feature Visibility admin tab, so an admin
 *   can always reach the controls to re-enable things. DevOps stays exempt too:
 *   it no longer holds the visibility controls, but it is the only route to
 *   backups and MCP tokens, and making it hideable is not this change's job;
 * - the product Overview tab, which is the universal redirect fallback when a
 *   hidden tab is opened directly — hiding it would drop the fallback from the bar;
 * - Home, which is where a hidden page redirects to. Hiding it would redirect
 *   the user off Home and straight back to it, forever.
 */
export const NON_HIDEABLE: ReadonlySet<string> = new Set<string>([
  navFeatureId('/'),
  navFeatureId('/admin'),
  adminTabFeatureId('feature-visibility'),
  adminTabFeatureId('devops'),
  productTabFeatureId('overview'),
]);

/** True when `id` is currently hidden AND is allowed to be hidden. */
export function isFeatureHidden(id: string, hidden: ReadonlySet<string>): boolean {
  if (NON_HIDEABLE.has(id)) return false;
  return hidden.has(id);
}
